import type { MomentEventKind } from "../game/types";

// Звуки событий. Синтезируются процедурно в AudioBuffer при инициализации —
// ассеты не нужны. Ключевое требование (GDD 4.8): при обратном ходе звук
// проигрывается ЗАДОМ НАПЕРЁД — это реверс того же буфера.
// Громкость ниже голоса ребёнка, чтобы не мешать распознаванию.

type Synth = (ac: OfflineAudioContext) => void;

const MASTER_GAIN = 0.28;

function noiseBurst(dur: number, filterHz: number, sweep = 0): Synth {
  return (ac) => {
    const n = Math.floor(ac.sampleRate * dur);
    const buf = ac.createBuffer(1, n, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const f = ac.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = filterHz;
    if (sweep) f.frequency.linearRampToValueAtTime(filterHz + sweep, dur);
    src.connect(f).connect(ac.destination);
    src.start(0);
  };
}

function tone(freq: number, dur: number, type: OscillatorType = "sine", glide = 0): Synth {
  return (ac) => {
    const osc = ac.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    if (glide) osc.frequency.linearRampToValueAtTime(freq + glide, dur);
    const g = ac.createGain();
    g.gain.value = 0.0001;
    g.gain.linearRampToValueAtTime(0.9, Math.min(0.02, dur * 0.2));
    g.gain.exponentialRampToValueAtTime(0.0001, dur);
    osc.connect(g).connect(ac.destination);
    osc.start(0);
    osc.stop(dur);
  };
}

function layer(...synths: Synth[]): Synth {
  return (ac) => synths.forEach((s) => s(ac));
}

// Рецепты звуков на каждый вид события.
const RECIPES: Partial<Record<MomentEventKind, { dur: number; make: Synth }>> = {
  tick: { dur: 0.06, make: tone(1200, 0.05, "square") },
  snailStep: { dur: 0.12, make: tone(180, 0.12, "sine", 40) },
  snailEat: { dur: 0.22, make: noiseBurst(0.22, 900) },
  snailHungry: { dur: 0.3, make: tone(300, 0.3, "sine", -120) },
  frogJump: { dur: 0.28, make: layer(tone(220, 0.26, "sine", 500), noiseBurst(0.18, 500)) },
  frogDive: { dur: 0.3, make: noiseBurst(0.3, 700, -400) },
  frogCatch: { dur: 0.16, make: tone(160, 0.16, "sawtooth", -40) },
  henFly: { dur: 0.3, make: noiseBurst(0.3, 400) },
  henReturn: { dur: 0.22, make: tone(500, 0.2, "triangle", -200) },
  eggWarm: { dur: 0.05, make: tone(2000, 0.04, "sine") },
  eggHatch: { dur: 0.32, make: layer(noiseBurst(0.22, 1600), tone(800, 0.3, "triangle", 300)) },
  chickCry: { dur: 0.2, make: tone(1500, 0.2, "sine", 400) },
  chickUnderWing: { dur: 0.3, make: tone(420, 0.3, "sine", -60) },
  flowerOpen: { dur: 0.4, make: tone(900, 0.4, "sine", 500) },
  flowerClose: { dur: 0.4, make: tone(1400, 0.4, "sine", -500) },
  cuckoo: { dur: 0.01, make: () => {} },
};

export class Sfx {
  private ac: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers = new Map<MomentEventKind, AudioBuffer>();
  private ready = false;

  async unlock() {
    if (!this.ac) {
      const AC: typeof AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.ac = new AC();
      this.master = this.ac.createGain();
      this.master.gain.value = MASTER_GAIN;
      this.master.connect(this.ac.destination);
    }
    if (this.ac.state === "suspended") await this.ac.resume();
    if (!this.ready) await this.render();
  }

  private async render() {
    const rate = this.ac!.sampleRate;
    for (const [kind, recipe] of Object.entries(RECIPES) as [MomentEventKind, { dur: number; make: Synth }][]) {
      if (recipe.dur < 0.02) continue;
      const off = new OfflineAudioContext(1, Math.ceil(rate * recipe.dur), rate);
      recipe.make(off);
      const rendered = await off.startRendering();
      this.buffers.set(kind, rendered);
    }
    this.ready = true;
  }

  private reverse(buf: AudioBuffer): AudioBuffer {
    const out = this.ac!.createBuffer(buf.numberOfChannels, buf.length, buf.sampleRate);
    for (let c = 0; c < buf.numberOfChannels; c++) {
      const s = buf.getChannelData(c);
      const d = out.getChannelData(c);
      for (let i = 0, j = s.length - 1; i < s.length; i++, j--) d[i] = s[j];
    }
    return out;
  }

  play(kind: MomentEventKind, reversed = false) {
    if (!this.ac || !this.master) return;
    const buf = this.buffers.get(kind);
    if (!buf) return;
    const node = this.ac.createBufferSource();
    node.buffer = reversed ? this.reverse(buf) : buf;
    node.connect(this.master);
    node.start();
  }

  /** Свист перемотки — короткий шум с обраткой. */
  rewind() {
    this.play("frogDive", true);
  }
}
